import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminApi, Client, ClientInTier, ClientTier, ClientTierFeeRule, CreateTierFeeRuleRequest, CreateDynamicTierRequest } from '../api/admin';
import { providersApi, Provider } from '../api/providers';
import { Plus, Edit2, Trash2, ChevronDown, ChevronUp, Users, Settings, Check, X, PauseCircle } from 'lucide-react';
import { getClientDisplayName } from '../utils/clientDisplay';
import { isCurrencyCloudProviderName } from '../utils/sepaIban';

const FEE_TYPES = [
  { value: 'OUTGOING_PAYMENT', label: 'Outgoing Payment', description: 'Fees for outgoing transfers' },
  { value: 'INCOMING_PAYMENT', label: 'Incoming Payment', description: 'Fees for incoming payments' },
  { value: 'DIGITAL_OUTGOING', label: 'OFT Outgoing', description: 'Fees for OFT outgoing transfers' },
  { value: 'DIGITAL_INCOMING', label: 'OFT Incoming', description: 'Fees for OFT incoming payments' },
  { value: 'INTERNAL_TRANSFER', label: 'Internal Transfer (XChangeLabPay)', description: 'Fees for internal client-to-client transfers' },
  { value: 'FX_MARKUP', label: 'FX Markup', description: 'Markup on currency exchange' },
];

const TIER_COLORS = [
  { value: 'bg-gray-100 text-gray-800', label: 'Gray' },
  { value: 'bg-blue-100 text-blue-800', label: 'Blue' },
  { value: 'bg-green-100 text-green-800', label: 'Green' },
  { value: 'bg-yellow-100 text-yellow-800', label: 'Yellow' },
  { value: 'bg-orange-100 text-orange-800', label: 'Orange' },
  { value: 'bg-red-100 text-red-800', label: 'Red' },
  { value: 'bg-purple-100 text-purple-800', label: 'Purple' },
  { value: 'bg-pink-100 text-pink-800', label: 'Pink' },
];

const TIER_ICONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
  { value: 'platinum', label: 'Platinum' },
  { value: 'vip', label: 'VIP' },
  { value: 'business', label: 'Business' },
  { value: 'enterprise', label: 'Enterprise' },
];

type FeeDecimalField = 'fixedAmount' | 'percentOfAmount' | 'minFee' | 'maxFee';

const DECIMAL_INPUT_REGEX = /^\d*([.,]\d*)?$/;

const formatFeeDecimalDraft = (value: number, emptyWhenZero = false): string => {
  if (emptyWhenZero && value === 0) return '';
  return String(value);
};

const parseFeeDecimalDraft = (value: string): number => {
  if (!value) return 0;
  const normalized = value.replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

function FeesPage() {
  const [activeTab, setActiveTab] = useState<'tiers' | 'rules'>('tiers');
  const [tiers, setTiers] = useState<ClientTier[]>([]);
  const [rules, setRules] = useState<ClientTierFeeRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fee Rules state
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [editingRule, setEditingRule] = useState<ClientTierFeeRule | null>(null);
  const [expandedTierId, setExpandedTierId] = useState<string | null>(null);
  const [feeFormValidationErrors, setFeeFormValidationErrors] = useState<string[]>([]);

  // Tiers state
  const [showTierForm, setShowTierForm] = useState(false);
  const [editingTier, setEditingTier] = useState<ClientTier | null>(null);
  const [showInactiveTiers, setShowInactiveTiers] = useState(false);
  const [expandedTierDetailId, setExpandedTierDetailId] = useState<string | null>(null);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [tierClientsById, setTierClientsById] = useState<Record<string, ClientInTier[]>>({});
  const [selectedClientIdsByTier, setSelectedClientIdsByTier] = useState<Record<string, string[]>>({});
  const [tierClientSearchById, setTierClientSearchById] = useState<Record<string, string>>({});
  const [tierClientsLoadingId, setTierClientsLoadingId] = useState<string | null>(null);
  const [tierClientsBusyId, setTierClientsBusyId] = useState<string | null>(null);
  const [feeCurrencyFilter, setFeeCurrencyFilter] = useState('');
  const [feeModeFilter, setFeeModeFilter] = useState<'ALL' | 'SAME_AS_PAYMENT' | 'FIXED_CURRENCY'>('ALL');
  const [feePaymentMethodFilter, setFeePaymentMethodFilter] = useState<'ALL' | 'SWIFT' | 'SEPA'>('ALL');

  // Fee Rule form state
  const [feeFormData, setFeeFormData] = useState<CreateTierFeeRuleRequest>({
    tierId: '',
    feeType: 'OUTGOING_PAYMENT',
    calculationMode: 'PERCENTAGE',
    fixedAmount: 0,
    percentOfAmount: 0.5,
    calculationPercentage: 0.5,
    calculationFixedAmount: 0,
    minFee: 5,
    maxFee: 100,
    reason: '',
    effectiveFrom: new Date().toISOString().slice(0, 16),
  });
  const [feeDecimalDrafts, setFeeDecimalDrafts] = useState<Record<FeeDecimalField, string>>({
    fixedAmount: '',
    percentOfAmount: '0.5',
    minFee: '5',
    maxFee: '100',
  });

  const isSepaFeeMethodAllowed = useCallback((data: CreateTierFeeRuleRequest) => {
    const provider = providers.find((item) => item.id === data.providerId);
    const isExternalPayment = data.feeType === 'INCOMING_PAYMENT' || data.feeType === 'OUTGOING_PAYMENT';
    const isEurPayment = data.paymentCurrencyId?.trim().toUpperCase() === 'EUR';
    const isEurFeeCurrency = !data.currencyId || data.currencyId.trim().toUpperCase() === 'EUR';

    return isExternalPayment
      && isCurrencyCloudProviderName(provider?.name)
      && isEurPayment
      && isEurFeeCurrency;
  }, [providers]);

  const keepAllowedPaymentMethod = useCallback((data: CreateTierFeeRuleRequest): CreateTierFeeRuleRequest => (
    data.paymentMethod === 'SEPA' && !isSepaFeeMethodAllowed(data)
      ? { ...data, paymentMethod: undefined }
      : data
  ), [isSepaFeeMethodAllowed]);

  // Tier form state
  const [tierFormData, setTierFormData] = useState<CreateDynamicTierRequest>({
    code: '',
    name: '',
    description: '',
    color: 'bg-blue-100 text-blue-800',
    icon: 'standard',
    priority: 100,
    maxClients: 0,
    requirements: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [tiersRes, rulesRes, clientsRes, providersList] = await Promise.all([
        adminApi.getAllTiers(showInactiveTiers),
        adminApi.getTierFeeRules(),
        adminApi.getClients().catch(() => ({ data: [] as Client[] })),
        providersApi.getAll().catch(() => [] as Provider[]),
      ]);
      setTiers(tiersRes.data || []);
      setRules(rulesRes.data || []);
      setAllClients(clientsRes.data || []);
      setProviders(providersList || []);
      if (tiersRes.data?.length && !feeFormData.tierId) {
        setFeeFormData(prev => ({ ...prev, tierId: tiersRes.data[0].id }));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [showInactiveTiers, feeFormData.tierId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clientsById = useMemo(() => {
    const map = new Map<string, Client>();
    for (const c of allClients) {
      if (c?.id) map.set(c.id, c);
    }
    return map;
  }, [allClients]);

  const formatClientName = (client: Pick<ClientInTier, 'id' | 'firstName' | 'lastName' | 'companyName' | 'email'>) => {
    const fromDirectory = client.id ? clientsById.get(client.id) : undefined;
    return getClientDisplayName({
      id: client.id,
      email: client.email || fromDirectory?.email,
      firstName: client.firstName || fromDirectory?.firstName,
      lastName: client.lastName || fromDirectory?.lastName,
      companyName: client.companyName || fromDirectory?.companyName,
      applicationType: fromDirectory?.applicationType,
    });
  };

  const loadTierClients = useCallback(async (tierId: string, force = false) => {
    if (!force && tierClientsById[tierId]) return;
    try {
      setTierClientsLoadingId(tierId);
      const res = await adminApi.getClientsInTier(tierId);
      setTierClientsById(prev => ({ ...prev, [tierId]: res.data || [] }));
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load tier clients');
    } finally {
      setTierClientsLoadingId(current => (current === tierId ? null : current));
    }
  }, [tierClientsById]);

  useEffect(() => {
    if (activeTab !== 'tiers' || !expandedTierDetailId) return;
    loadTierClients(expandedTierDetailId);
  }, [activeTab, expandedTierDetailId, loadTierClients]);

  // Fee Rule handlers
  const validateFeeForm = useCallback((data: CreateTierFeeRuleRequest): { errors: string[]; fields: Set<string> } => {
    const errors: string[] = [];
    const fields = new Set<string>();
    const addError = (field: string, message: string) => {
      errors.push(message);
      fields.add(field);
    };

    if (!data.feeType) addError('feeType', 'Fee Type is required.');
    if (!data.calculationMode) addError('calculationMode', 'Calculation Mode is required.');
    if (!data.effectiveFrom) addError('effectiveFrom', 'Effective From is required.');

    if (data.calculationMode === 'PERCENTAGE' || data.calculationMode === 'PERCENTAGE_PLUS_FIXED') {
      if (data.calculationMode === 'PERCENTAGE_PLUS_FIXED' && data.percentOfAmount <= 0) addError('percentOfAmount', 'Percentage must be greater than zero.');
      if (data.calculationMode === 'PERCENTAGE' && data.percentOfAmount < 0) addError('percentOfAmount', 'Percentage cannot be negative.');
      if (data.percentOfAmount > 100) addError('percentOfAmount', 'Percentage cannot be greater than 100%.');
    }

    if ((data.feeCurrencyMode || 'SAME_AS_PAYMENT') === 'FIXED_CURRENCY') {
      // red.2: FIXED_CURRENCY cancelled — ignore mode lock
    }

    if (data.calculationMode === 'FIXED' && data.fixedAmount < 0) {
      addError('fixedAmount', 'Fixed Amount cannot be negative.');
    }

    if (data.calculationMode === 'PERCENTAGE_PLUS_FIXED' && data.fixedAmount <= 0) {
      addError('fixedAmount', 'Fixed Amount must be greater than zero.');
    }

    if (data.calculationMode === 'PERCENTAGE_PLUS_FIXED' && data.feeType === 'FX_MARKUP') {
      addError('calculationMode', 'Percentage + Fixed is not available for FX Markup.');
    }

    if (!data.reason?.trim()) addError('reason', 'Reason is required.');

    if (data.minFee < 0) addError('minFee', 'Min Fee cannot be negative.');
    if (data.maxFee < 0) addError('maxFee', 'Max Fee cannot be negative.');
    if (data.maxFee > 0 && data.maxFee < data.minFee) {
      addError('minFee', 'Max Fee must be greater than or equal to Min Fee.');
      addError('maxFee', 'Max Fee must be greater than or equal to Min Fee.');
    }

    if ((data.minAmount ?? 0) < 0) addError('minAmount', 'Min Payment Amount cannot be negative.');
    if ((data.maxAmount ?? 0) < 0) addError('maxAmount', 'Max Payment Amount cannot be negative.');
    if (data.minAmount != null && data.maxAmount != null && data.maxAmount < data.minAmount) {
      addError('minAmount', 'Max Payment Amount must be greater than or equal to Min Payment Amount.');
      addError('maxAmount', 'Max Payment Amount must be greater than or equal to Min Payment Amount.');
    }

    const currencyPattern = /^[A-Z0-9]{3,10}$/;
    if (data.currencyId && !currencyPattern.test(data.currencyId.toUpperCase())) {
      addError('currencyId', 'Fee Currency must contain 3-10 uppercase letters/numbers.');
    }
    if (data.paymentCurrencyId && !currencyPattern.test(data.paymentCurrencyId.toUpperCase())) {
      addError('paymentCurrencyId', 'Payment Currency must contain 3-10 uppercase letters/numbers.');
    }

    if (data.paymentMethod === 'SEPA' && !isSepaFeeMethodAllowed(data)) {
      addError('paymentMethod', 'SEPA is available only for Currency Cloud EUR incoming and outgoing payment fees.');
      const provider = providers.find((item) => item.id === data.providerId);
      if (!isCurrencyCloudProviderName(provider?.name)) {
        addError('providerId', 'SEPA requires the Currency Cloud provider.');
      }
      if (data.feeType !== 'INCOMING_PAYMENT' && data.feeType !== 'OUTGOING_PAYMENT') {
        addError('feeType', 'SEPA is available only for incoming and outgoing payment fees.');
      }
      if (data.paymentCurrencyId?.toUpperCase() !== 'EUR') {
        addError('paymentCurrencyId', 'SEPA requires EUR as the payment currency.');
      }
      if (data.currencyId && data.currencyId.toUpperCase() !== 'EUR') {
        addError('currencyId', 'SEPA requires EUR as the fee currency when it is specified.');
      }
    }

    const from = data.effectiveFrom ? new Date(data.effectiveFrom) : null;
    const to = data.effectiveTo ? new Date(data.effectiveTo) : null;
    if (from && Number.isNaN(from.getTime())) addError('effectiveFrom', 'Effective From is invalid.');
    if (to && Number.isNaN(to.getTime())) addError('effectiveTo', 'Effective To is invalid.');
    if (from && to && to <= from) {
      addError('effectiveFrom', 'Effective To must be later than Effective From.');
      addError('effectiveTo', 'Effective To must be later than Effective From.');
    }

    if (data.description && data.description.length > 500) {
      addError('description', 'Description cannot exceed 500 characters.');
    }

    return { errors, fields };
  }, [isSepaFeeMethodAllowed, providers]);

  const feeFormFieldErrors = useMemo(
    () => validateFeeForm(feeFormData).fields,
    [feeFormData, validateFeeForm]
  );

  const feeFieldClass = (field: string) =>
    `w-full border rounded-lg px-3 py-2 ${
      feeFormFieldErrors.has(field) ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  const handleFeeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedFeeData: CreateTierFeeRuleRequest = {
      ...feeFormData,
      fixedAmount: parseFeeDecimalDraft(feeDecimalDrafts.fixedAmount),
      percentOfAmount: parseFeeDecimalDraft(feeDecimalDrafts.percentOfAmount),
      calculationFixedAmount: parseFeeDecimalDraft(feeDecimalDrafts.fixedAmount),
      calculationPercentage: parseFeeDecimalDraft(feeDecimalDrafts.percentOfAmount),
      minFee: parseFeeDecimalDraft(feeDecimalDrafts.minFee),
      maxFee: parseFeeDecimalDraft(feeDecimalDrafts.maxFee),
    };
    const validation = validateFeeForm(normalizedFeeData);
    if (validation.errors.length > 0) {
      setFeeFormValidationErrors(validation.errors);
      return;
    }
    try {
      setFeeFormValidationErrors([]);
      if (editingRule) {
        await adminApi.updateTierFeeRule(editingRule.id, { ...normalizedFeeData, id: editingRule.id, isActive: true });
      } else {
        await adminApi.createTierFeeRule(normalizedFeeData);
      }
      setShowFeeForm(false);
      setEditingRule(null);
      resetFeeForm();
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save rule');
    }
  };

  const handleDeactivateRule = async (id: string) => {
    if (!window.confirm('Deactivate this fee rule?')) return;
    try {
      await adminApi.deactivateTierFeeRule(id);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate rule');
    }
  };

  const handleDeleteRule = async (id: string) => {
    if (!window.confirm('Permanently delete this fee rule?')) return;
    try {
      await adminApi.deleteTierFeeRule(id);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete rule');
    }
  };

  const handleEditRule = (rule: ClientTierFeeRule) => {
    setEditingRule(rule);
    setFeeFormValidationErrors([]);
    setFeeFormData({
      providerId: rule.providerId,
      tierId: rule.tierId,
      feeType: rule.feeType,
      calculationMode: rule.calculationMode,
      fixedAmount: rule.fixedAmount,
      percentOfAmount: rule.percentOfAmount,
      calculationFixedAmount: rule.calculationFixedAmount ?? rule.fixedAmount,
      calculationPercentage: rule.calculationPercentage ?? rule.percentOfAmount,
      minFee: rule.minFee,
      maxFee: rule.maxFee,
      currencyId: rule.currencyId,
      feeCurrencyMode: rule.feeCurrencyMode || 'SAME_AS_PAYMENT',
      minAmount: rule.minAmount,
      maxAmount: rule.maxAmount,
      paymentCurrencyId: rule.paymentCurrencyId,
      effectiveFrom: rule.effectiveFrom.slice(0, 16),
      effectiveTo: rule.effectiveTo?.slice(0, 16),
      description: rule.description,
      reason: '',
    });
    setFeeDecimalDrafts({
      fixedAmount: formatFeeDecimalDraft(rule.fixedAmount, true),
      percentOfAmount: formatFeeDecimalDraft(rule.percentOfAmount),
      minFee: formatFeeDecimalDraft(rule.minFee, true),
      maxFee: formatFeeDecimalDraft(rule.maxFee, true),
    });
    setShowFeeForm(true);
  };

  const resetFeeForm = () => {
    const resetData: CreateTierFeeRuleRequest = {
      tierId: tiers[0]?.id || '',
      feeType: 'OUTGOING_PAYMENT',
      calculationMode: 'PERCENTAGE',
      fixedAmount: 0,
      percentOfAmount: 0.5,
      calculationPercentage: 0.5,
      calculationFixedAmount: 0,
      minFee: 5,
      maxFee: 100,
      reason: '',
      effectiveFrom: new Date().toISOString().slice(0, 16),
    };
    setFeeFormData(resetData);
    setFeeDecimalDrafts({
      fixedAmount: formatFeeDecimalDraft(resetData.fixedAmount, true),
      percentOfAmount: formatFeeDecimalDraft(resetData.percentOfAmount),
      minFee: formatFeeDecimalDraft(resetData.minFee, true),
      maxFee: formatFeeDecimalDraft(resetData.maxFee, true),
    });
    setFeeFormValidationErrors([]);
  };

  const handleFeeDecimalFieldChange = (
    field: FeeDecimalField,
    rawValue: string,
    emptyWhenZero = false
  ) => {
    if (!DECIMAL_INPUT_REGEX.test(rawValue)) return;

    setFeeDecimalDrafts(prev => ({ ...prev, [field]: rawValue }));

    if (rawValue === '') {
      setFeeFormData(prev => ({ ...prev, [field]: 0 }));
      return;
    }

    const normalized = rawValue.replace(',', '.');
    if (normalized.endsWith('.')) return;

    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) return;

    setFeeFormData(prev => ({ ...prev, [field]: parsed }));

    if (emptyWhenZero && parsed === 0) {
      setFeeDecimalDrafts(prev => ({ ...prev, [field]: '' }));
    }
  };

  const commitFeeDecimalField = (field: FeeDecimalField, emptyWhenZero = false) => {
    const parsed = parseFeeDecimalDraft(feeDecimalDrafts[field]);
    setFeeFormData(prev => ({ ...prev, [field]: parsed }));
    setFeeDecimalDrafts(prev => ({
      ...prev,
      [field]: formatFeeDecimalDraft(parsed, emptyWhenZero),
    }));
  };

  // Tier handlers
  const handleTierSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTier) {
        await adminApi.updateTier(editingTier.id, {
          ...tierFormData,
          isActive: editingTier.isActive,
          priority: tierFormData.priority ?? 100,
        });
      } else {
        await adminApi.createTier(tierFormData);
      }
      setShowTierForm(false);
      setEditingTier(null);
      resetTierForm();
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save tier');
    }
  };

  const handleDeleteTier = async (id: string) => {
    if (!window.confirm('Permanently delete this tier? All clients will be unassigned.')) return;
    try {
      await adminApi.deleteTier(id);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete tier');
    }
  };

  const handleActivateTier = async (id: string) => {
    try {
      await adminApi.activateTier(id);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to activate tier');
    }
  };

  const handleDeactivateTier = async (id: string) => {
    if (!window.confirm('Deactivate this tier?')) return;
    try {
      await adminApi.deactivateTier(id);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate tier');
    }
  };

  const handleEditTier = (tier: ClientTier) => {
    setEditingTier(tier);
    setTierFormData({
      code: tier.code,
      name: tier.name,
      description: tier.description || '',
      color: tier.color || 'bg-blue-100 text-blue-800',
      icon: tier.icon || 'standard',
      priority: tier.priority,
      maxClients: tier.maxClients || 0,
      requirements: tier.requirements || '',
    });
    setShowTierForm(true);
  };

  const toggleClientSelection = (tierId: string, clientId: string) => {
    setSelectedClientIdsByTier(prev => {
      const selected = new Set(prev[tierId] || []);
      if (selected.has(clientId)) selected.delete(clientId);
      else selected.add(clientId);
      return { ...prev, [tierId]: Array.from(selected) };
    });
  };

  const handleBulkAddClientsToTier = async (tierId: string) => {
    const clientIds = selectedClientIdsByTier[tierId] || [];
    if (clientIds.length === 0) return;
    try {
      setTierClientsBusyId(tierId);
      await adminApi.bulkAssignClientsToTier(tierId, clientIds, 'Added from Fees page');
      setSelectedClientIdsByTier(prev => ({ ...prev, [tierId]: [] }));
      await Promise.all([loadData(), loadTierClients(tierId, true)]);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.response?.data || err.message || 'Failed to add clients to tier';
      setError(String(message));
    } finally {
      setTierClientsBusyId(current => (current === tierId ? null : current));
    }
  };

  const handleRemoveClientFromTier = async (tierId: string, clientId: string) => {
    if (!window.confirm('Remove client from this category? Client will be moved to default category.')) return;
    try {
      setTierClientsBusyId(tierId);
      await adminApi.removeClientFromTier(tierId, clientId);
      await Promise.all([loadData(), loadTierClients(tierId, true)]);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.response?.data || err.message || 'Failed to remove client from tier';
      setError(String(message));
    } finally {
      setTierClientsBusyId(current => (current === tierId ? null : current));
    }
  };

  const resetTierForm = () => {
    setTierFormData({
      code: '',
      name: '',
      description: '',
      color: 'bg-blue-100 text-blue-800',
      icon: 'standard',
      priority: 100,
      maxClients: 0,
      requirements: '',
    });
  };

  const getFeeTypeLabel = (type: string) => FEE_TYPES.find(t => t.value === type)?.label || type;

  const tierIds = new Set(tiers.map(t => t.id));
  const filteredActiveRules = useMemo(() => {
    const currency = feeCurrencyFilter.trim().toUpperCase();
    return rules.filter(r => {
      if (!r.isActive) return false;
      if (feeModeFilter !== 'ALL' && (r.feeCurrencyMode || 'SAME_AS_PAYMENT') !== feeModeFilter) {
        return false;
      }
      if (currency && !(r.currencyId || '').toUpperCase().includes(currency)) {
        return false;
      }
      if (feePaymentMethodFilter !== 'ALL' && (r.paymentMethod || '').toUpperCase() !== feePaymentMethodFilter) {
        return false;
      }
      return true;
    });
  }, [rules, feeCurrencyFilter, feeModeFilter, feePaymentMethodFilter]);

  const defaultRules = filteredActiveRules.filter(r => !r.tierId || !tierIds.has(r.tierId));

  const rulesByTier: Array<{ tierId: string; tierName: string; tierCode: string; color: string | undefined; rules: ClientTierFeeRule[]; isDefault: boolean }> = [
    {
      tierId: '__default__',
      tierName: 'Default (No Tier)',
      tierCode: 'DEFAULT',
      color: 'bg-gray-100 text-gray-700',
      rules: defaultRules,
      isDefault: true,
    },
    ...tiers.map(tier => ({
      tierId: tier.id,
      tierName: tier.name,
      tierCode: tier.code,
      color: tier.color,
      rules: filteredActiveRules.filter(r => r.tierId === tier.id),
      isDefault: false,
    })),
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Fees & Commissions</h1>
          <p className="text-gray-600">Manage client tiers and tier-based fee rules</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('tiers')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'tiers'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Client Tiers
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'rules'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Fee Rules
          </button>
        </nav>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 text-red-700 p-4 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-sm underline">Dismiss</button>
        </div>
      )}

      {/* Tiers Tab */}
      {activeTab === 'tiers' && (
        <>
          <div className="mb-6 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showInactiveTiers}
                onChange={e => setShowInactiveTiers(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show inactive
            </label>
            <button
              onClick={() => { setShowTierForm(true); setEditingTier(null); resetTierForm(); }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Tier
            </button>
          </div>

          {/* Tier Form */}
          {showTierForm && (
            <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">
                {editingTier ? 'Edit Tier' : 'Create New Tier'}
              </h2>
              <form onSubmit={handleTierSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    value={tierFormData.code}
                    onChange={e => setTierFormData({ ...tierFormData, code: e.target.value.toUpperCase() })}
                    placeholder="STD"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={tierFormData.name}
                    onChange={e => setTierFormData({ ...tierFormData, name: e.target.value })}
                    placeholder="Standard"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={tierFormData.description}
                    onChange={e => setTierFormData({ ...tierFormData, description: e.target.value })}
                    placeholder="Standard tier for regular clients"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <select
                    value={tierFormData.color}
                    onChange={e => setTierFormData({ ...tierFormData, color: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {TIER_COLORS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  <select
                    value={tierFormData.icon}
                    onChange={e => setTierFormData({ ...tierFormData, icon: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {TIER_ICONS.map(i => (
                      <option key={i.value} value={i.value}>{i.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <input
                    type="number"
                    value={tierFormData.priority}
                    onChange={e => setTierFormData({ ...tierFormData, priority: parseInt(e.target.value) || 0 })}
                    placeholder="100"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Clients (0 = unlimited)</label>
                  <input
                    type="number"
                    value={tierFormData.maxClients}
                    onChange={e => setTierFormData({ ...tierFormData, maxClients: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label>
                  <textarea
                    value={tierFormData.requirements}
                    onChange={e => setTierFormData({ ...tierFormData, requirements: e.target.value })}
                    placeholder="Minimum deposit: $1000, KYC verified"
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div className="col-span-1 md:col-span-2 flex gap-3">
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                  >
                    {editingTier ? 'Update Tier' : 'Create Tier'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowTierForm(false); setEditingTier(null); }}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tier List */}
          <div className="space-y-4">
            {tiers.map(tier => {
              const tierClients = tierClientsById[tier.id] || [];
              const selectedClientIds = selectedClientIdsByTier[tier.id] || [];
              const tierSearch = (tierClientSearchById[tier.id] || '').trim().toLowerCase();
              const tierClientIdSet = new Set(tierClients.map(c => c.id));
              const clientsToAdd = allClients.filter(client => {
                const isActive = (client.status || '').trim().toUpperCase() === 'ACTIVE';
                return isActive && !tierClientIdSet.has(client.id);
              });
              const filteredClientsToAdd = tierSearch
                ? clientsToAdd.filter(client =>
                    [client.firstName, client.lastName, client.companyName, client.email, client.publicId]
                      .filter(Boolean)
                      .some(v => String(v).toLowerCase().includes(tierSearch)))
                : clientsToAdd;
              const maxClientsToShow = filteredClientsToAdd.slice(0, 30);

              return (
              <div key={tier.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setExpandedTierDetailId(expandedTierDetailId === tier.id ? null : tier.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${tier.color || 'bg-gray-100 text-gray-800'}`}>
                      {tier.name}
                    </span>
                    <span className="text-sm text-gray-500">{tier.code}</span>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Users className="w-4 h-4" />
                      {tier.clientCount || 0}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Settings className="w-4 h-4" />
                      {tier.feeRuleCount || 0} rules
                    </div>
                    {!tier.isActive && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded-full">Inactive</span>
                    )}
                    {tier.isSystem && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-600 rounded-full">System</span>
                    )}
                  </div>
                  {expandedTierDetailId === tier.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>

                {expandedTierDetailId === tier.id && (
                  <div className="border-t border-gray-200 px-6 py-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Priority</div>
                        <div className="font-medium">{tier.priority}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Max Clients</div>
                        <div className="font-medium">{tier.maxClients || 'Unlimited'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Icon</div>
                        <div className="font-medium capitalize">{tier.icon}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Created</div>
                        <div className="font-medium">{new Date(tier.createdAt).toLocaleDateString()}</div>
                      </div>
                    </div>

                    {tier.description && (
                      <div className="mb-4">
                        <div className="text-xs text-gray-500 uppercase mb-1">Description</div>
                        <div className="text-sm text-gray-700">{tier.description}</div>
                      </div>
                    )}

                    {tier.requirements && (
                      <div className="mb-4">
                        <div className="text-xs text-gray-500 uppercase mb-1">Requirements</div>
                        <div className="text-sm text-gray-700 whitespace-pre-line">{tier.requirements}</div>
                      </div>
                    )}

                    <div className="mb-5 rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900">Manage Clients In Category</h4>
                        <span className="text-xs text-gray-500">{tierClients.length} in category</span>
                      </div>

                      <div className="mb-3">
                        <input
                          type="text"
                          value={tierClientSearchById[tier.id] || ''}
                          onChange={e => setTierClientSearchById(prev => ({ ...prev, [tier.id]: e.target.value }))}
                          placeholder="Search clients to add (name, email, publicId)"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>

                      <div className="mb-3 max-h-44 overflow-y-auto rounded border border-gray-200">
                        {maxClientsToShow.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500">No clients available for adding.</div>
                        ) : (
                          maxClientsToShow.map(client => {
                            const clientName = formatClientName(client);
                            const checked = selectedClientIds.includes(client.id);
                            return (
                              <label key={client.id} className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                <div className="flex items-center gap-2 min-w-0">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleClientSelection(tier.id, client.id)}
                                    className="rounded border-gray-300"
                                  />
                                  <div className="min-w-0">
                                    <div className="text-sm text-gray-800 truncate">{clientName}</div>
                                    <div className="text-xs text-gray-500 truncate">{client.email}</div>
                                  </div>
                                </div>
                                {client.publicId && <div className="text-xs text-gray-400 font-mono">{client.publicId}</div>}
                              </label>
                            );
                          })
                        )}
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <div className="text-xs text-gray-500">
                          Selected: {selectedClientIds.length}
                          {filteredClientsToAdd.length > maxClientsToShow.length ? ` • showing first ${maxClientsToShow.length}` : ''}
                        </div>
                        <button
                          onClick={() => handleBulkAddClientsToTier(tier.id)}
                          disabled={selectedClientIds.length === 0 || tierClientsBusyId === tier.id}
                          className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {tierClientsBusyId === tier.id ? 'Adding...' : 'Add Selected'}
                        </button>
                      </div>

                      <div className="rounded border border-gray-200">
                        <div className="px-3 py-2 text-xs font-medium uppercase text-gray-500 border-b border-gray-200">
                          Clients in this category
                        </div>
                        {tierClientsLoadingId === tier.id ? (
                          <div className="p-3 text-sm text-gray-500">Loading clients...</div>
                        ) : tierClients.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500">No clients assigned to this category.</div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto">
                            {tierClients.map(client => (
                              <div key={client.id} className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0">
                                <div className="min-w-0">
                                  <div className="text-sm text-gray-800 truncate">{formatClientName(client)}</div>
                                  <div className="text-xs text-gray-500 truncate">{client.email}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {client.publicId && <span className="text-xs text-gray-400 font-mono">{client.publicId}</span>}
                                  {!tier.isSystem && (
                                    <button
                                      onClick={() => handleRemoveClientFromTier(tier.id, client.id)}
                                      disabled={tierClientsBusyId === tier.id}
                                      className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditTier(tier)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                      {!tier.isSystem && (
                        <>
                          {tier.isActive ? (
                            <button
                              onClick={() => handleDeactivateTier(tier.id)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            >
                              <X className="w-4 h-4" />
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivateTier(tier.id)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            >
                              <Check className="w-4 h-4" />
                              Activate
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteTier(tier.id)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
            })}
          </div>

          {tiers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No tiers found. Create your first tier to get started.
            </div>
          )}
        </>
      )}

      {/* Fee Rules Tab */}
      {activeTab === 'rules' && (
        <>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Fee currency</label>
                <input
                  type="text"
                  placeholder="USD"
                  value={feeCurrencyFilter}
                  onChange={e => setFeeCurrencyFilter(e.target.value.toUpperCase())}
                  className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Fee currency mode</label>
                <select
                  value={feeModeFilter}
                  onChange={e => setFeeModeFilter(e.target.value as 'ALL' | 'SAME_AS_PAYMENT' | 'FIXED_CURRENCY')}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="ALL">All modes</option>
                  <option value="SAME_AS_PAYMENT">SAME_AS_PAYMENT</option>
                  <option value="FIXED_CURRENCY">FIXED_CURRENCY</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Payment method</label>
                <select
                  value={feePaymentMethodFilter}
                  onChange={e => setFeePaymentMethodFilter(e.target.value as 'ALL' | 'SWIFT' | 'SEPA')}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="ALL">All methods</option>
                  <option value="SWIFT">SWIFT</option>
                  <option value="SEPA">SEPA</option>
                </select>
              </div>
            </div>
            <button
              onClick={() => { setShowFeeForm(true); setEditingRule(null); resetFeeForm(); }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Fee Rule
            </button>
          </div>

          {/* Fee Rule Form */}
          {showFeeForm && (
            <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">
                {editingRule ? 'Edit Fee Rule' : 'Create New Fee Rule'}
              </h2>
              {feeFormValidationErrors.length > 0 && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                  <div className="text-sm font-medium text-red-700 mb-1">Please fix the following:</div>
                  <ul className="list-disc ml-5 text-sm text-red-700">
                    {feeFormValidationErrors.map((msg, idx) => (
                      <li key={`${msg}-${idx}`}>{msg}</li>
                    ))}
                  </ul>
                </div>
              )}
              <form onSubmit={handleFeeSubmit} className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Tier</label>
                  <select
                    value={feeFormData.tierId}
                    onChange={e => setFeeFormData({ ...feeFormData, tierId: e.target.value })}
                    className={feeFieldClass('tierId')}
                  >
                    <option value="">Default (No Tier)</option>
                    {tiers.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fee Type</label>
                  <select
                    value={feeFormData.feeType}
                    onChange={e => {
                      const feeType = e.target.value as CreateTierFeeRuleRequest['feeType'];
                      setFeeFormData(keepAllowedPaymentMethod({
                        ...feeFormData,
                        feeType,
                        calculationMode: feeType === 'FX_MARKUP' && feeFormData.calculationMode === 'PERCENTAGE_PLUS_FIXED'
                          ? 'PERCENTAGE'
                          : feeFormData.calculationMode
                      }));
                    }}
                    className={feeFieldClass('feeType')}
                  >
                    {FEE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calculation Mode</label>
                  <select
                    value={feeFormData.calculationMode}
                    onChange={e => setFeeFormData({ ...feeFormData, calculationMode: e.target.value as any })}
                    className={feeFieldClass('calculationMode')}
                  >
                    <option value="FIXED">Fixed Amount</option>
                    <option value="PERCENTAGE">Percentage</option>
                    {feeFormData.feeType !== 'FX_MARKUP' && (
                      <option value="PERCENTAGE_PLUS_FIXED">Percentage + Fixed</option>
                    )}
                  </select>
                </div>

                {(feeFormData.calculationMode === 'FIXED' || feeFormData.calculationMode === 'PERCENTAGE_PLUS_FIXED') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Amount</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={feeDecimalDrafts.fixedAmount}
                      onChange={e => handleFeeDecimalFieldChange('fixedAmount', e.target.value, true)}
                      onBlur={() => commitFeeDecimalField('fixedAmount', true)}
                      placeholder="0.00"
                      className={feeFieldClass('fixedAmount')}
                    />
                  </div>
                )}

                {(feeFormData.calculationMode === 'PERCENTAGE' || feeFormData.calculationMode === 'PERCENTAGE_PLUS_FIXED') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentage (%)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={feeDecimalDrafts.percentOfAmount}
                      onChange={e => handleFeeDecimalFieldChange('percentOfAmount', e.target.value)}
                      onBlur={() => commitFeeDecimalField('percentOfAmount')}
                      placeholder="0.5"
                      className={feeFieldClass('percentOfAmount')}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Fee</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={feeDecimalDrafts.minFee}
                    onChange={e => handleFeeDecimalFieldChange('minFee', e.target.value, true)}
                    onBlur={() => commitFeeDecimalField('minFee', true)}
                    placeholder="0"
                    className={feeFieldClass('minFee')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Fee (0 = no cap)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={feeDecimalDrafts.maxFee}
                    onChange={e => handleFeeDecimalFieldChange('maxFee', e.target.value, true)}
                    onBlur={() => commitFeeDecimalField('maxFee', true)}
                    placeholder="0 (no cap)"
                    className={feeFieldClass('maxFee')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fee Currency (optional, rate currency)</label>
                  <input
                    type="text"
                    placeholder="USD"
                    value={feeFormData.currencyId || ''}
                    onChange={e => setFeeFormData(keepAllowedPaymentMethod({
                      ...feeFormData,
                      currencyId: e.target.value.toUpperCase(),
                      feeCurrencyMode: 'SAME_AS_PAYMENT',
                    }))}
                    className={feeFieldClass('currencyId')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Currency (optional)</label>
                  <input
                    type="text"
                    placeholder="EUR"
                    value={feeFormData.paymentCurrencyId || ''}
                    onChange={e => setFeeFormData(keepAllowedPaymentMethod({
                      ...feeFormData,
                      paymentCurrencyId: e.target.value.toUpperCase() || undefined,
                    }))}
                    className={feeFieldClass('paymentCurrencyId')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                  <select
                    value={feeFormData.providerId || ''}
                    onChange={e => {
                      const providerId = e.target.value || undefined;
                      setFeeFormData(keepAllowedPaymentMethod({
                        ...feeFormData,
                        providerId,
                      }));
                    }}
                    className={feeFieldClass('providerId')}
                  >
                    <option value="">Any provider</option>
                    {providers.map(provider => (
                      <option key={provider.id} value={provider.id}>{provider.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select
                    value={feeFormData.paymentMethod || ''}
                    disabled={!isCurrencyCloudProviderName(providers.find(item => item.id === feeFormData.providerId)?.name)}
                    onChange={e => setFeeFormData({ ...feeFormData, paymentMethod: (e.target.value || undefined) as 'SWIFT' | 'SEPA' | undefined })}
                    className={feeFieldClass('paymentMethod')}
                  >
                    <option value="">Any</option>
                    <option value="SWIFT">SWIFT</option>
                    <option value="SEPA" disabled={!isSepaFeeMethodAllowed(feeFormData)}>
                      SEPA
                    </option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    SEPA requires Currency Cloud, incoming or outgoing payment type, EUR payment currency, and an empty or EUR fee currency.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Payment Amount (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0"
                    value={feeFormData.minAmount ?? ''}
                    onChange={e => setFeeFormData({ ...feeFormData, minAmount: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className={feeFieldClass('minAmount')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Payment Amount (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="No limit"
                    value={feeFormData.maxAmount ?? ''}
                    onChange={e => setFeeFormData({ ...feeFormData, maxAmount: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className={feeFieldClass('maxAmount')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Effective From</label>
                  <input
                    type="datetime-local"
                    value={feeFormData.effectiveFrom}
                    onChange={e => setFeeFormData({ ...feeFormData, effectiveFrom: e.target.value })}
                    className={feeFieldClass('effectiveFrom')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Effective To (optional)</label>
                  <input
                    type="datetime-local"
                    value={feeFormData.effectiveTo || ''}
                    onChange={e => setFeeFormData({ ...feeFormData, effectiveTo: e.target.value || undefined })}
                    className={feeFieldClass('effectiveTo')}
                  />
                </div>

                <div className="col-span-2 md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Change Reason</label>
                  <input
                    type="text"
                    value={feeFormData.reason}
                    onChange={e => setFeeFormData({ ...feeFormData, reason: e.target.value })}
                    className={feeFieldClass('reason')}
                  />
                </div>

                <div className="col-span-2 md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    placeholder="e.g., VIP discount 50% off standard rate"
                    value={feeFormData.description || ''}
                    onChange={e => setFeeFormData({ ...feeFormData, description: e.target.value })}
                    className={feeFieldClass('description')}
                  />
                </div>

                <div className="col-span-2 md:col-span-3 flex gap-3">
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                  >
                    {editingRule ? 'Update Rule' : 'Create Rule'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowFeeForm(false); setEditingRule(null); setFeeFormValidationErrors([]); }}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Fee Rules List */}
          <div className="space-y-4">
            {rulesByTier.map(({ tierId, tierName, tierCode, color, rules: tierRules, isDefault }) => (
              <div key={tierId} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setExpandedTierId(expandedTierId === tierId ? null : tierId)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${color}`}>
                      {tierName}
                    </span>
                    <span className="text-gray-500">
                      {tierRules.length} active rule{tierRules.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {expandedTierId === tierId ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>

                {expandedTierId === tierId && (
                  <div className="border-t border-gray-200">
                    <div className="flex justify-end px-6 py-3 border-b border-gray-100">
                      <button
                        onClick={() => {
                          setShowFeeForm(true);
                          setEditingRule(null);
                          resetFeeForm();
                          if (tierId !== '__default__') {
                            setFeeFormData(prev => ({ ...prev, tierId }));
                          }
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                      >
                        <Plus className="w-3 h-3" /> Add Rule
                      </button>
                    </div>
                    {tierRules.length === 0 ? (
                      <div className="p-6 text-gray-500 text-center">
                        {isDefault
                          ? 'No default fee rules configured. System built-in defaults will apply.'
                          : `No active fee rules for ${tierName} tier. Default fees will apply.`}
                      </div>
                    ) : (
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fee Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode / Currency</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calculation</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min / Max</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {tierRules.map(rule => (
                            <tr key={rule.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="font-medium text-gray-900">{getFeeTypeLabel(rule.feeType)}</div>
                                {rule.providerName && (
                                  <div className="text-sm text-gray-500">Provider: {rule.providerName}</div>
                                )}
                                {rule.description && (
                                  <div className="text-sm text-gray-500">{rule.description}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 text-sm">
                                <span className={rule.paymentMethod ? 'rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-800' : 'text-gray-500'}>
                                  {rule.paymentMethod || 'Any'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                <div>{rule.feeCurrencyMode || 'SAME_AS_PAYMENT'}</div>
                                <div className="text-gray-500">{rule.currencyId || '—'}</div>
                              </td>
                              <td className="px-6 py-4">
                                {rule.calculationMode === 'FIXED' ? (
                                  <span className="font-medium">{rule.fixedAmount.toFixed(2)}</span>
                                ) : rule.calculationMode === 'PERCENTAGE_PLUS_FIXED' ? (
                                  <span className="font-medium">
                                    {rule.calculationPercentage}% + {(rule.calculationFixedAmount ?? rule.fixedAmount).toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="font-medium">{rule.percentOfAmount}%</span>
                                )}
                                {rule.currencyId && (
                                  <span className="text-gray-500 ml-1">{rule.currencyId}</span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">
                                Min: {rule.currencyId || '$'}{rule.minFee.toFixed(2)}<br />
                                Max: {rule.maxFee > 0 ? `${rule.currencyId || '$'}${rule.maxFee.toFixed(2)}` : 'No cap'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">
                                {new Date(rule.effectiveFrom).toLocaleDateString()}
                                {rule.effectiveTo && (
                                  <span> - {new Date(rule.effectiveTo).toLocaleDateString()}</span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleEditRule(rule)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeactivateRule(rule.id)}
                                    className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                    title="Deactivate"
                                  >
                                    <PauseCircle className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRule(rule.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">How Tier-Based Fees Work</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Clients are assigned a tier (Standard, Silver, Gold, Platinum, VIP)</li>
              <li>• Each tier can have custom fee rules for outgoing payments, incoming payments, and FX markup</li>
              <li>• If no tier-specific rule exists, the system falls back to provider defaults</li>
              <li>• Rules can be provider-specific or global (apply to all providers)</li>
              <li>• Min/Max fee caps ensure fees stay within reasonable bounds</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export default FeesPage;
